import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { PermissionLevel } from '../types/permissions';
import { UserDto } from '../services/usersService';
import SalesRepPicker from './partialSupervisor/SalesRepPicker';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background-color: var(--surface-color);
  border-radius: 8px;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  
  &:hover {
    color: var(--text-primary);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 1rem;
  direction: rtl;
  text-align: right;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(0, 142, 162, 0.1);
  }
  
  &.error {
    border-color: var(--error-color);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 1rem;
  direction: rtl;
  text-align: right;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(0, 142, 162, 0.1);
  }
  
  &.error {
    border-color: var(--error-color);
  }
`;

const ErrorMessage = styled.span`
  color: var(--error-color);
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const SuccessMessage = styled.span`
  color: var(--success-color);
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const HelperText = styled.span`
  color: var(--text-secondary);
  font-size: 0.78rem;
  margin-top: 0.25rem;
  font-style: italic;
`;

const GeneralError = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 16px;
  border: 1px solid var(--error-color);
  font-size: 14px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  ${props => props.$variant === 'primary' ? `
    background-color: var(--primary-color);
    color: white;
    
    &:hover:not(:disabled) {
      background-color: var(--primary-hover);
    }
  ` : `
    background-color: transparent;
    color: var(--secondary-color);
    border: 1px solid var(--secondary-color);
    
    &:hover:not(:disabled) {
      background-color: var(--hover-background);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export interface User {
  id?: number;
  fullName: string;
  email: string;
  phone: string;
  permissionLevel: number;
  isSupervisor: boolean;
  isPartialSupervisor: boolean;
  isActive: boolean;
  defaultCommissionFixedAmount: number;
  defaultCommissionPercentage: number;
}

interface UserFormProps {
  user?: User;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (user: User, password?: string, salesRepIds?: number[], groupName?: string) => Promise<void>;
  isLoading?: boolean;
}

const UserForm: React.FC<UserFormProps> = ({
  user,
  isOpen,
  onClose,
  onSubmit,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<User & { password?: string }>({
    fullName: '',
    email: '',
    phone: '',
    permissionLevel: 3,
    isSupervisor: false,
    isPartialSupervisor: false,
    isActive: true,
    defaultCommissionFixedAmount: 0,
    defaultCommissionPercentage: 0,
    password: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string>('');

  // מפקח חלקי — בורר נציגים (ביצירה בלבד); מוצג לתפקיד נציג מכירות כשהדגל מסומן
  const [selectedSalesReps, setSelectedSalesReps] = useState<UserDto[]>([]);
  const [groupName, setGroupName] = useState<string>('');
  const isSalesRep = formData.permissionLevel === PermissionLevel.SalesRep;

  // Update form data when user prop changes
  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName,
        email: user.email,
        phone: user.phone,
        permissionLevel: user.permissionLevel,
        isSupervisor: user.isSupervisor,
        isPartialSupervisor: user.isPartialSupervisor,
        isActive: user.isActive,
        defaultCommissionFixedAmount: user.defaultCommissionFixedAmount ?? 0,
        defaultCommissionPercentage: user.defaultCommissionPercentage ?? 0,
        password: ''
      });
    } else {
      // Reset form for new user
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        permissionLevel: 3,
        isSupervisor: false,
        isPartialSupervisor: false,
        isActive: true,
        defaultCommissionFixedAmount: 0,
        defaultCommissionPercentage: 0,
        password: ''
      });
    }
    // Clear any previous errors
    setErrors({});
    setGeneralError('');
    // Reset partial-supervisor picker state (assignment is done on create only)
    setSelectedSalesReps([]);
    setGroupName('');
  }, [user, isOpen]);

  // Validate a single field (for onBlur validation)
  const validateField = (field: string, value: any): string => {
    switch (field) {
      case 'fullName':
        if (!value || !value.trim()) {
          return 'שם מלא הוא שדה חובה';
        }
        break;

      case 'email':
        if (!value || !value.trim()) {
          return 'דוא"ל הוא שדה חובה';
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          return 'פורמט הדוא"ל אינו תקין';
        }
        break;

      case 'phone':
        if (!value || !value.trim()) {
          return 'טלפון הוא שדה חובה';
        } else if (value.trim().length < 9) {
          return 'מספר טלפון חייב להכיל לפחות 9 ספרות';
        }
        break;

      case 'password':
        if (!user && (!value || value.length < 6)) {
          return 'סיסמה חייבת להכיל לפחות 6 תווים';
        }
        break;

      case 'defaultCommissionFixedAmount':
        if (value !== '' && value !== null && value !== undefined && Number(value) < 0) {
          return 'סכום קבוע חייב להיות חיובי';
        }
        break;

      case 'defaultCommissionPercentage':
        if (value !== '' && value !== null && value !== undefined && (Number(value) < 0 || Number(value) > 100)) {
          return 'אחוז עמלה חייב להיות בין 0 ל-100';
        }
        break;

      default:
        return '';
    }
    return '';
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    const fullNameError = validateField('fullName', formData.fullName);
    if (fullNameError) newErrors.fullName = fullNameError;

    const emailError = validateField('email', formData.email);
    if (emailError) newErrors.email = emailError;

    const phoneError = validateField('phone', formData.phone);
    if (phoneError) newErrors.phone = phoneError;

    if (!user) {
      const passwordError = validateField('password', formData.password);
      if (passwordError) newErrors.password = passwordError;
    }

    if (isSalesRep) {
      const fixedError = validateField('defaultCommissionFixedAmount', formData.defaultCommissionFixedAmount);
      if (fixedError) newErrors.defaultCommissionFixedAmount = fixedError;
      const percentError = validateField('defaultCommissionPercentage', formData.defaultCommissionPercentage);
      if (percentError) newErrors.defaultCommissionPercentage = percentError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setGeneralError(''); // Clear previous errors
      const isCreatingPartial = !user && isSalesRep && formData.isPartialSupervisor;
      const salesRepIds = isCreatingPartial ? selectedSalesReps.map(r => r.id) : undefined;
      const trimmedGroupName = isCreatingPartial && groupName.trim() ? groupName.trim() : undefined;
      await onSubmit(formData, formData.password, salesRepIds, trimmedGroupName);
      // Don't call onClose() here - let the parent component close it after successful API call
    } catch (error: any) {
      console.error('Error submitting form:', error);

      // Extract error message from server response
      let errorMessage = 'שגיאה בשמירת הנתונים';

      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages: string[] = [];
        Object.values(validationErrors).forEach((value: any) => {
          if (Array.isArray(value)) {
            errorMessages.push(...value);
          } else {
            errorMessages.push(String(value));
          }
        });
        errorMessage = errorMessages.join(', ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setGeneralError(errorMessage);
    }
  };

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleBlur = (field: string, value: any) => {
    const error = validateField(field, value);
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    } else {
      // Clear error if field is now valid
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const isFieldValid = (field: string, value: any): boolean => {
    return !validateField(field, value) && value && value.toString().trim() !== '';
  };

  // Track where mousedown started to prevent closing when selecting text
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && mouseDownTargetRef.current === e.currentTarget) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  if (!isOpen) return null;

  return (
    <Overlay onMouseDown={handleMouseDown} onClick={handleOverlayClick}>
      <Modal>
        <ModalHeader>
          <ModalTitle>{user ? 'עריכת משתמש' : 'הוספת משתמש חדש'}</ModalTitle>
          <CloseButton onClick={onClose} type="button">×</CloseButton>
        </ModalHeader>
        
        <Form onSubmit={handleSubmit}>
          {generalError && (
            <GeneralError>{generalError}</GeneralError>
          )}

          <FormGroup>
            <Label>שם מלא *</Label>
            <Input
              type="text"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              onBlur={(e) => handleBlur('fullName', e.target.value)}
              className={errors.fullName ? 'error' : ''}
              disabled={isLoading}
            />
            {errors.fullName && <ErrorMessage>{errors.fullName}</ErrorMessage>}
            {!errors.fullName && isFieldValid('fullName', formData.fullName) && (
              <SuccessMessage>✓ תקין</SuccessMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label>דוא"ל *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onBlur={(e) => handleBlur('email', e.target.value)}
              className={errors.email ? 'error' : ''}
              disabled={isLoading}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            {!errors.email && isFieldValid('email', formData.email) && (
              <SuccessMessage>✓ כתובת דוא"ל תקינה</SuccessMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label>טלפון *</Label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              onBlur={(e) => handleBlur('phone', e.target.value)}
              className={errors.phone ? 'error' : ''}
              disabled={isLoading}
            />
            {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
            {!errors.phone && isFieldValid('phone', formData.phone) && (
              <SuccessMessage>✓ מספר טלפון תקין</SuccessMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label>רמת הרשאה *</Label>
            <Select
              value={formData.permissionLevel}
              onChange={(e) => handleInputChange('permissionLevel', parseInt(e.target.value))}
              disabled={isLoading || !!user}
            >
              <option value={1}>מנהל מערכת</option>
              <option value={2}>נציג מכירות</option>
              <option value={3}>מנהל תיקים</option>
              <option value={4}>מזכירה</option>
            </Select>
            {!!user && (
              <HelperText>לא ניתן לשנות תפקיד לאחר יצירת המשתמש</HelperText>
            )}
          </FormGroup>

          {/* נציג מכירות: מפקח מלא / מפקח חלקי — זרים הדדית */}
          {formData.permissionLevel === 2 && (
            <FormGroup>
              <Label>
                <input
                  type="checkbox"
                  checked={formData.isSupervisor}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    isSupervisor: e.target.checked,
                    isPartialSupervisor: e.target.checked ? false : prev.isPartialSupervisor,
                  }))}
                  disabled={isLoading}
                  style={{ marginLeft: '0.5rem' }}
                />
                מפקח
              </Label>
              <Label>
                <input
                  type="checkbox"
                  checked={formData.isPartialSupervisor}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    isPartialSupervisor: e.target.checked,
                    isSupervisor: e.target.checked ? false : prev.isSupervisor,
                  }))}
                  disabled={isLoading}
                  style={{ marginLeft: '0.5rem' }}
                />
                מפקח חלקי
              </Label>
            </FormGroup>
          )}

          {/* נציג מכירות: עמלת ברירת מחדל (סכום קבוע + אחוז) — מועתקת לליד בעת הקצאה */}
          {isSalesRep && (
            <FormGroup>
              <Label>עמלת נציג (ברירת מחדל)</Label>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <Label style={{ fontWeight: 400, fontSize: '0.8rem' }}>סכום קבוע (₪)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    style={{ width: '150px' }}
                    value={formData.defaultCommissionFixedAmount}
                    onChange={(e) => handleInputChange('defaultCommissionFixedAmount', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                    onBlur={(e) => handleBlur('defaultCommissionFixedAmount', e.target.value)}
                    className={errors.defaultCommissionFixedAmount ? 'error' : ''}
                    disabled={isLoading}
                  />
                  {errors.defaultCommissionFixedAmount && <ErrorMessage>{errors.defaultCommissionFixedAmount}</ErrorMessage>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <Label style={{ fontWeight: 400, fontSize: '0.8rem' }}>אחוז (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    style={{ width: '120px' }}
                    value={formData.defaultCommissionPercentage}
                    onChange={(e) => handleInputChange('defaultCommissionPercentage', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                    onBlur={(e) => handleBlur('defaultCommissionPercentage', e.target.value)}
                    className={errors.defaultCommissionPercentage ? 'error' : ''}
                    disabled={isLoading}
                  />
                  {errors.defaultCommissionPercentage && <ErrorMessage>{errors.defaultCommissionPercentage}</ErrorMessage>}
                </div>
              </div>
              <HelperText>ערכים אלה יועתקו אוטומטית לליד חדש שיוקצה לנציג, וניתנים לעריכה בכל ליד</HelperText>
            </FormGroup>
          )}

          {/* מנהל תיקים: מפקח (מלא) */}
          {formData.permissionLevel === 3 && (
            <FormGroup>
              <Label>
                <input
                  type="checkbox"
                  checked={formData.isSupervisor}
                  onChange={(e) => handleInputChange('isSupervisor', e.target.checked)}
                  disabled={isLoading}
                  style={{ marginLeft: '0.5rem' }}
                />
                מפקח
              </Label>
            </FormGroup>
          )}

          {/* מפקח חלקי — שיוך נציגים ביצירה בלבד (לקיים: דרך מודאל ניהול נציגים) */}
          {!user && isSalesRep && formData.isPartialSupervisor && (
            <>
              <FormGroup>
                <Label>נציגי מכירות מפוקחים</Label>
                <SalesRepPicker
                  selectedSalesReps={selectedSalesReps}
                  onChange={setSelectedSalesReps}
                  disabled={isLoading}
                />
              </FormGroup>
              <FormGroup>
                <Label>שם קבוצה (אופציונלי)</Label>
                <Input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  disabled={isLoading}
                  placeholder="לדוגמה: צוות צפון"
                />
              </FormGroup>
            </>
          )}

          {!user && (
            <FormGroup>
              <Label>סיסמה *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                onBlur={(e) => handleBlur('password', e.target.value)}
                className={errors.password ? 'error' : ''}
                disabled={isLoading}
                placeholder="הזן סיסמה (לפחות 6 תווים)"
              />
              {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
              {!errors.password && formData.password && formData.password.length >= 6 && (
                <SuccessMessage>✓ סיסמה תקינה</SuccessMessage>
              )}
            </FormGroup>
          )}

          {user && (
            <FormGroup>
              <Label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  disabled={isLoading}
                  style={{ marginLeft: '0.5rem' }}
                />
                משתמש פעיל
              </Label>
            </FormGroup>
          )}

          <ButtonGroup>
            <Button type="button" onClick={onClose} disabled={isLoading}>
              ביטול
            </Button>
            <Button type="submit" $variant="primary" disabled={isLoading}>
              {isLoading ? 'שומר...' : user ? 'עדכן' : 'הוסף'}
            </Button>
          </ButtonGroup>
        </Form>
      </Modal>
    </Overlay>
  );
};

export default UserForm;